// Clients Module
import { api } from '../core/api-client.js';
import { showNotification } from '../utils/notifications.js';
import { hasPermission } from '../utils/permissions.js';

export async function loadClients() {
    if (!hasPermission('view:billing')) {
        console.log('[Clients] No permission to view clients');
        return;
    }

    try {
        let clients = await api.getClients();
        console.log('[Clients] Raw response:', clients);

        // Normalize response
        if (!Array.isArray(clients)) {
            if (clients.data && Array.isArray(clients.data)) {
                clients = clients.data;
            } else if (clients.clients && Array.isArray(clients.clients)) {
                clients = clients.clients;
            } else {
                console.warn('[Clients] Unexpected response format, defaulting to empty array');
                clients = [];
            }
        }

        console.log('[Clients] Normalized:', clients);
        updateClientsUI(clients);
        updateClientStats(clients);
    } catch (error) {
        console.error('[Clients] Failed to load:', error);
        showNotification('Failed to load clients', 'error');
    }
}

export async function viewClient(id) {
    // Navigate to the full-page client detail view
    window.location.href = '/clients/' + id;
}

export function switchClientTab(tabName) {
    // Hide all tabs
    ['overview', 'servers', 'invoices'].forEach(t => {
        document.getElementById(`tab-content-${t}`).classList.add('hidden');
        const btn = document.getElementById(`tab-btn-${t}`);
        // Reset styles
        btn.classList.remove('text-white', 'border-b-2', 'border-neonCyan');
        btn.classList.add('text-slate-500');
    });

    // Show active tab
    document.getElementById(`tab-content-${tabName}`).classList.remove('hidden');
    const activeBtn = document.getElementById(`tab-btn-${tabName}`);
    activeBtn.classList.remove('text-slate-500');
    activeBtn.classList.add('text-white', 'border-b-2', 'border-neonCyan');
}

export async function triggerScan(agentId) {
    if (!confirm("Are you sure you want to trigger a remote security scan on this server?")) return;

    try {
        await api.triggerScan(agentId);
        showNotification('Scan command sent successfully', 'success');
    } catch (error) {
        console.error('Scan failed:', error);
        showNotification('Failed to trigger scan', 'error');
    }
}

// Attach globally
window.viewClient = viewClient;
window.switchClientTab = switchClientTab;
window.triggerScan = triggerScan;

function updateClientsUI(clients) {
    try {
        const tbody = document.getElementById('client-list-body');
        if (!tbody) return;

        tbody.innerHTML = clients.map(client => `
            <tr class="border-b border-slate-800 hover:bg-slate-900/50 cursor-pointer" onclick="viewClient(${client.id})">
                <td class="py-4 px-4 font-bold text-white">${client.name}</td>
                <td class="py-4 px-4 text-slate-500">${client.email || '-'}</td>
                <td class="py-4 px-4">
                    <span class="px-2 py-1 ${client.status === 'active' ? 'bg-green-900 text-green-300' : 'bg-slate-800 text-slate-400'} rounded text-xs">
                        ${client.status}
                    </span>
                </td>
                <td class="py-4 px-4 text-slate-500">${client.agents ? client.agents.length : 0} agents</td>
                <td class="py-4 px-4 text-right" onclick="event.stopPropagation()">
                    <button onclick="viewClient(${client.id})" class="text-neonCyan hover:text-white mr-2" title="View Details">
                        <i class="fas fa-eye"></i>
                    </button>
                    <button onclick="editClient(${client.id})" class="text-neonCyan hover:text-white mr-2" title="Edit Customer">
                        <i class="fas fa-edit"></i>
                    </button>
                    <button onclick="deleteClient(${client.id})" class="text-red-500 hover:text-red-400" title="Delete Customer">
                        <i class="fas fa-trash"></i>
                    </button>
                </td>
            </tr>
        `).join('');
    } catch (error) {
        console.error('[Clients] Error updating UI:', error);
    }
}


export async function editClient(id) {
    try {
        const client = await api.getClient(id);
        const modal = document.getElementById('editClientModal');
        const form = document.getElementById('editClientForm');

        if (modal && form) {
            form.id.value = client.id;
            form.name.value = client.name;
            form.email.value = client.email;
            form.phone.value = client.phone || '';
            form.address.value = client.address || '';
            if (form.notes) form.notes.value = client.notes || '';
            form.status.value = client.status;
            form.plan.value = client.plan || 'Trial';

            modal.classList.remove('hidden');
        } else {
            showNotification('Edit customer form not available on this page', 'error');
        }
    } catch (error) {
        console.error('[Clients] Failed to load client for editing:', error);
        showNotification('Failed to load client details', 'error');
    }
}

// Handle Edit Form Submit
document.addEventListener('submit', async (e) => {
    if (e.target && e.target.id === 'editClientForm') {
        e.preventDefault();
        const form = e.target;
        const id = form.id.value;

        const data = {
            name: form.name.value.trim(),
            email: form.email.value.trim(),
            phone: form.phone.value.trim(),
            address: form.address.value.trim(),
            notes: form.notes ? form.notes.value.trim() : '',
            status: form.status.value,
            plan: form.plan.value
        };

        try {
            await api.updateClient(id, data);
            showNotification('Client updated successfully', 'success');
            document.getElementById('editClientModal').classList.add('hidden');
            // Client detail is SSR — reload so notes panel updates when on that page
            if (document.getElementById('view-client-detail')) {
                location.reload();
            } else {
                loadClients();
            }
        } catch (error) {
            console.error('[Clients] Update failed:', error);
            showNotification('Failed to update client', 'error');
        }
    }
});

export async function deleteClient(id) {
    if (!confirm('Are you sure you want to delete this client? This will also remove all associated agents.')) return;

    try {
        await api.deleteClient(id);
        showNotification('Client deleted successfully', 'success');
        loadClients();
    } catch (error) {
        console.error('[Clients] Failed to delete:', error);
        showNotification('Failed to delete client: ' + error.message, 'error');
    }
}

window.editClient = editClient;
window.deleteClient = deleteClient;
window.loadClients = loadClients;

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    // Bind Create Client Form
    const createBtn = document.getElementById('addClientSubmitBtn');
    if (createBtn) {
        createBtn.addEventListener('click', handleCreateClient);
    }
});

async function handleCreateClient(e) {
    e.preventDefault();

    const form = document.getElementById('addClientForm');
    if (!form) return;

    // Basic validation
    const name = form.name.value.trim();
    const email = form.email.value.trim();

    if (!name || !email) {
        showNotification('Name and Email are required', 'error');
        return;
    }

    const clientData = {
        name: name,
        company: form.company.value.trim(),
        email: email,
        phone: form.phone.value.trim(),
        address: form.address.value.trim(),
        status: form.status.value || 'active'
    };

    try {
        await api.createClient(clientData);
        showNotification('Client created successfully', 'success');
        closeModal('addClientModal');
        form.reset();
        loadClients();
    } catch (error) {
        console.error('[Clients] Create failed:', error);
        showNotification(error.message || 'Failed to create client', 'error');
    }
}

function updateClientStats(clients) {
    console.log('[Clients] Updating stats with:', clients.length, 'clients');

    if (!Array.isArray(clients)) {
        console.warn('[Clients] Invalid data for stats update');
        return;
    }

    const total = clients.length;
    const active = clients.filter(c => c.status === 'active').length;
    const suspended = clients.filter(c => c.status === 'suspended').length;

    // Check for 'trial' status or if they are on a trial package (logic can be expanded)
    // For now, case-insensitive check for status
    const trial = clients.filter(c => (c.status || '').toLowerCase() === 'trial').length;

    const retention = total > 0 ? Math.round((active / total) * 100) : 0;

    console.log('[Clients] Stats calculated:', { total, active, suspended, trial, retention });

    setTextContent('stat-total-clients', total);
    setTextContent('stat-active-clients', active);
    setTextContent('stat-suspended-clients', suspended);
    setTextContent('stat-trial-clients', trial);
    setTextContent('text-retention-rate', `${retention}% retention`);
}

function setTextContent(id, value) {
    const el = document.getElementById(id);
    if (el) {
        el.textContent = value;
        // console.log(`[Clients] Updated ${id} to ${value}`);
    } else {
        console.warn(`[Clients] Element with ID ${id} not found`);
    }
}
